/* eslint-disable @altermaind/you-shall-not-pass */
import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Svg, Typography } from '@altermaind/web/ui';
import { useDrawer } from '@/_shared/components';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { CollapsableCard } from '@/_shared/components/CollapsableCard';
import { StyledTableDrawer, usePaginatedTable } from '@/_shared/components/Table';
import { Routes } from '@/_shared/constants';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { GenericApiError } from '@/_shared/types/apiError';
import { PAGE_SIZE_TEAM_MEMBERS } from '@/departments/state/constants';
import { getDepartmentTeamMembersCall, ResponseGetDepartmentTeams } from '../../state';
import { TableArea } from './TableArea';

type Props = {
  departmentId?: string;
  loading?: boolean;
  error?: GenericApiError | undefined;
  success?: boolean;
  responseGetDepartmentTeams?: ResponseGetDepartmentTeams;
};

export const TabDepartment: FC<Props> = ({
  departmentId,
  loading,
  error,
  success,
  responseGetDepartmentTeams,
}) => {
  const t = useTranslations('departments.details');
  const { genericResponsiveValue } = useResponsiveStyle();

  const tShared = useTranslations();
  const navigate = useNavigate();
  const { isOpen, close, ...drawerProps } = useDrawer();

  const {
    execute: executeGetDepartmentTeamMembers,
    loading: loadingGetDepartmentTeamMembers,
    error: errorGetDepartmentTeamMembers,
    data,
    totalItems,
  } = getDepartmentTeamMembersCall.hook();

  const paginedProps = usePaginatedTable({
    rowData: data || [],
    pageSize: PAGE_SIZE_TEAM_MEMBERS,
    totalItems: totalItems || 0,
  });

  const drawers: StyledTableDrawer['instances'] = [];

  const onOpenCollapsable = useCallback(
    (id: string) => {
      if (departmentId) {
        executeGetDepartmentTeamMembers({
          teamId: id,
          departmentId,
          excludeMemberType: ['departmentManager'],
        });
      }
    },
    [executeGetDepartmentTeamMembers, departmentId],
  );

  const goToPeople = useCallback(() => {
    navigate(Routes.People);
  }, [navigate]);

  const onTeamNameClick = useCallback(
    (teamId: string) => {
      if (!departmentId) {
        return;
      }
      navigate(
        Routes.DepartmentsTeamsDetail.replace(':departmentId', departmentId).replace(
          ':teamId',
          teamId,
        ),
      );
    },
    [departmentId, navigate],
  );

  if (loading) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (error) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (success && !loading && !error) {
    return (
      <Box>
        <Card mt='m' mb='s' display='flex' flexDirection='column'>
          <Box
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            alignItems={genericResponsiveValue({
              breakPoints: { desktop: 'center' },
              defaultValue: 'flex-start',
            })}
          >
            <Box
              display={genericResponsiveValue({
                breakPoints: { desktop: 'flex' },
                defaultValue: 'flex',
              })}
              mt={genericResponsiveValue({
                breakPoints: { desktop: 'none' },
                defaultValue: 'xs',
              })}
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'none' },
                defaultValue: 'column',
              })}
              justifyContent='space-between'
              width='100%'
              alignItems={genericResponsiveValue({
                breakPoints: { desktop: 'center' },
                defaultValue: 'flex-start',
              })}
            >
              <Typography variant='h1'>
                {responseGetDepartmentTeams?.department.name !== 'unknown'
                  ? responseGetDepartmentTeams?.department.name
                  : t('noDepartmentName')}
              </Typography>
              <Box
                ml={genericResponsiveValue({
                  breakPoints: { desktop: 'xl' },
                  defaultValue: 'none',
                })}
                alignItems='center'
                flexDirection='row'
                display='flex'
                flex={1}
                mt={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xs',
                })}
              >
                <Svg name='users' size='s' />
                <Typography ml='xxs' variant='h1'>
                  {responseGetDepartmentTeams?.department?.stats?.totalEmployees || 0}
                </Typography>
              </Box>
              <Button
                mt={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xs',
                })}
                variant='positive'
                onClick={goToPeople}
              >
                {t('goToPeople')}
              </Button>
            </Box>
          </Box>
          <Box
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            alignItems='flex-start'
            width={genericResponsiveValue({
              breakPoints: { desktop: '50%' },
              defaultValue: '100%',
            })}
            mb='s'
            backgroundColor={genericResponsiveValue({
              breakPoints: { desktop: 'transparent' },
              defaultValue: 'grey/dark',
            })}
            p={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 's',
            })}
            mt={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 'xs',
            })}
            borderRadius={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 'm',
            })}
          >
            <Box flex={1} flexDirection='column' display='flex'>
              <Typography
                mt={genericResponsiveValue({
                  breakPoints: { desktop: 'xs' },
                  defaultValue: 'none',
                })}
                variant='p2/light'
                color='grey/light'
              >
                {t('validProfiles')}
              </Typography>
              <Typography variant='h1'>
                {responseGetDepartmentTeams?.department.stats?.completedEmployees}
              </Typography>
            </Box>
            <Box flex={1} flexDirection='column' display='flex'>
              <Typography mt='xs' variant='p2/light' color='grey/light'>
                {t('profilesToCorrect')}
              </Typography>
              <Typography
                variant='h1'
                color={
                  responseGetDepartmentTeams?.department.stats?.invalidEmployees
                    ? 'error'
                    : 'success'
                }
              >
                {responseGetDepartmentTeams?.department.stats?.invalidEmployees || 0}
              </Typography>
            </Box>
          </Box>
          <Box display='flex' flexDirection='column'>
            <Typography mb='s' mt='xs' variant='p2/label'>
              {t('departmentManager')}
            </Typography>
            {responseGetDepartmentTeams?.department?.manager ? (
              <TableArea
                loading={loadingGetDepartmentTeamMembers}
                error={errorGetDepartmentTeamMembers}
                paginedProps={{
                  ...paginedProps,
                  paginatedData: [responseGetDepartmentTeams?.department?.manager],
                }}
                drawer={{ ...drawerProps, isOpen, close, instances: drawers }}
                hidePagination
              />
            ) : (
              <AlertBadge iconName='lightBulb' type='error' label={t('managerNotExist')} />
            )}
          </Box>
        </Card>
        <Typography my='xs' variant='h2'>
          {t('team')}
        </Typography>
        {responseGetDepartmentTeams &&
          responseGetDepartmentTeams.data.map(team => (
            <CollapsableCard
              key={team.name}
              mb='xs'
              title={team.id !== 'unknown' ? team.name : t('noTeamName')}
              dataTestIdButton='teams-collapsable-card-button'
              onCustomToggle={() => onOpenCollapsable(team.id)}
              titleProps={{
                onClick: () => (team.id !== 'unknown' ? onTeamNameClick(team.id) : null),
                style: { cursor: 'pointer' },
              }}
              closeOthersCollapseOnOpen
              onTitleHoverStyle={{ textDecoration: team.id !== 'unknown' ? 'underline' : 'none' }}
            >
              <Box
                width='100%'
                minWidth={0}
                minHeight={0}
                overflow={genericResponsiveValue({
                  breakPoints: { desktop: 'visible' },
                  defaultValue: 'auto',
                })}
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <Box
                  alignItems='center'
                  mb='s'
                  display='flex'
                  flexDirection='row'
                  justifyContent='flex-end'
                  width='100%'
                >
                  <Box
                    display='flex'
                    width={genericResponsiveValue({
                      breakPoints: { desktop: 'auto' },
                      defaultValue: '100%',
                    })}
                    flexDirection={genericResponsiveValue({
                      breakPoints: { desktop: 'row' },
                      defaultValue: 'column',
                    })}
                    alignItems={genericResponsiveValue({
                      breakPoints: { desktop: 'center' },
                      defaultValue: 'flex-start',
                    })}
                    mt={genericResponsiveValue({
                      breakPoints: { desktop: 'xs' },
                      defaultValue: 's',
                    })}
                  >
                    <Typography variant='p3/label'>
                      {t('manager')}
                      <Typography variant='p2/light' as='span'>
                        {team.manager ? `${team.manager.firstName} ${team.manager.lastName}` : '-'}
                      </Typography>
                    </Typography>
                    <Box
                      ml={genericResponsiveValue({
                        breakPoints: { desktop: 'xl' },
                        defaultValue: 'none',
                      })}
                      alignItems='center'
                      flexDirection='row'
                      display='flex'
                      mt={genericResponsiveValue({
                        breakPoints: { desktop: 'none' },
                        defaultValue: 'xxs',
                      })}
                    >
                      <Svg name='users' size='s' />
                      <Typography ml='xxs' variant='h1'>
                        {totalItems || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {team.id !== 'unknown' && !team.manager ? (
                  <AlertBadge iconName='lightBulb' type='error' label={t('managerNotExist')} />
                ) : null}
                <TableArea
                  loading={loadingGetDepartmentTeamMembers}
                  error={errorGetDepartmentTeamMembers}
                  paginedProps={paginedProps}
                  drawer={{ ...drawerProps, isOpen, close, instances: drawers }}
                />
              </Box>
            </CollapsableCard>
          ))}
      </Box>
    );
  }

  return null;
};
